import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import { 
  ArrowLeft, Edit2, Save, X, Loader2, FileText, Download, Clock, AlertTriangle,
  Sparkles, Mail, Shield, Phone, Car, Cloud, Zap, Printer, File, RefreshCw, History, Users,
  Trash2, Copy, Archive, MoreVertical
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { ResiliationClientCard, ResiliationClientUpload } from "../../components/engagements/ResiliationClient";
import VehicleValidationDialog from "../../components/engagements/VehicleValidationDialog";

const API = process.env.REACT_APP_BACKEND_URL;

// Import lucide icons for categories
import { 
  Landmark,      // Bank/Credit
  CreditCard,    // Credit
  Wrench,        // Maintenance
  Building2,     // Location/Real estate
  Briefcase,     // Services
  Newspaper      // Subscriptions
} from "lucide-react";

const categoryIcons = {
  pret: { icon: Landmark, color: "text-emerald-600", bg: "bg-emerald-100", label: "Prêt bancaire" },
  credit: { icon: CreditCard, color: "text-teal-600", bg: "bg-teal-100", label: "Crédit" },
  assurance: { icon: Shield, color: "text-blue-600", bg: "bg-blue-100", label: "Assurance" },
  telephonie: { icon: Phone, color: "text-purple-600", bg: "bg-purple-100", label: "Téléphonie" },
  leasing: { icon: Car, color: "text-red-600", bg: "bg-red-100", label: "Leasing" },
  saas: { icon: Cloud, color: "text-cyan-600", bg: "bg-cyan-100", label: "SaaS" },
  energie: { icon: Zap, color: "text-amber-600", bg: "bg-amber-100", label: "Énergie" },
  materiel: { icon: Printer, color: "text-gray-600", bg: "bg-gray-200", label: "Matériel" },
  maintenance: { icon: Wrench, color: "text-orange-600", bg: "bg-orange-100", label: "Maintenance" },
  location: { icon: Building2, color: "text-indigo-600", bg: "bg-indigo-100", label: "Location" },
  services: { icon: Briefcase, color: "text-pink-600", bg: "bg-pink-100", label: "Services" },
  abonnement: { icon: Newspaper, color: "text-violet-600", bg: "bg-violet-100", label: "Abonnement" },
  autre: { icon: File, color: "text-slate-600", bg: "bg-slate-200", label: "Autre" }
};

const formatCurrency = (amount) => {
  if (!amount) return "-";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("fr-FR");
  } catch {
    return dateStr;
  }
};

export default function ContratDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contrat, setContrat] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [regenerating, setRegenerating] = useState(false);
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [showConcurrenceModal, setShowConcurrenceModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  
  // Loan-specific states
  const [showSimulationModal, setShowSimulationModal] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [capitalInput, setCapitalInput] = useState("");
  const [vehicleLink, setVehicleLink] = useState(null); // { linked, vehicle, vehicle_contract }
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);

  useEffect(() => {
    fetchContrat();
    fetchVehicleLink();
  }, [id]);

  const fetchVehicleLink = async () => {
    try {
      const res = await axios.get(`${API}/api/engagements/contrats/${id}/vehicle-link`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicleLink(res.data);
    } catch (e) {
      // pas grave si l'appel échoue
    }
  };

  const fetchContrat = async () => {
    try {
      const res = await axios.get(`${API}/api/engagements/contrats/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContrat(res.data);
      setFormData(res.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Contrat non trouvé");
      navigate("/engagements/contrats");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/engagements/contrats/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contrat mis à jour");
      setEditMode(false);
      fetchContrat();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateSummary = async () => {
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/api/engagements/contrats/${id}/regenerate-summary`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContrat(prev => ({ ...prev, resume_ia: res.data.resume_ia }));
      toast.success("Résumé régénéré");
    } catch (error) {
      toast.error("Erreur lors de la régénération");
    } finally {
      setRegenerating(false);
    }
  };

  const [reExtracting, setReExtracting] = useState(false);
  const handleReExtractAmounts = async () => {
    setReExtracting(true);
    try {
      const res = await axios.post(`${API}/api/engagements/contrats/${id}/re-extract-amounts`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setContrat(prev => ({
          ...prev,
          montant_mensuel_ht: res.data.montant_mensuel_ht,
          montant_annuel_ht: res.data.montant_annuel_ht,
        }));
        toast.success(res.data.message || "Montants ré-extraits");
      } else {
        toast.info(res.data.message || "Aucun montant détecté");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de ré-extraction");
    } finally {
      setReExtracting(false);
    }
  };

  const handleGenerateResiliation = async () => {
    setGeneratingLetter(true);
    try {
      const res = await axios.post(`${API}/api/engagements/resiliations/generate?contrat_id=${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lettre de résiliation générée");
      navigate(`/engagements/resiliations?id=${res.data.resiliation_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de génération");
    } finally {
      setGeneratingLetter(false);
    }
  };

  const handleConcurrenceIntent = async () => {
    try {
      await axios.post(`${API}/api/engagements/concurrence/intent`, {
        contrat_id: id,
        categorie: contrat.categorie
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Intérêt enregistré !");
      setShowConcurrenceModal(false);
    } catch (error) {
      toast.error("Erreur");
    }
  };

  // === LOAN-SPECIFIC FUNCTIONS ===
  const handleSimulateRepayment = async () => {
    setSimulationLoading(true);
    try {
      const capital = capitalInput || contrat.capital_restant_du || contrat.capital_emprunte;
      const res = await axios.post(
        `${API}/api/engagements/contrats/${id}/simulation-remboursement?capital_restant_du=${capital}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSimulation(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de simulation");
    } finally {
      setSimulationLoading(false);
    }
  };

  const handleGenerateEarlyRepaymentLetter = async () => {
    setGeneratingLetter(true);
    try {
      const capital = capitalInput || simulation?.capital_restant_du || contrat.capital_restant_du || contrat.capital_emprunte;
      let url = `${API}/api/engagements/contrats/${id}/demande-remboursement-anticipe`;
      if (capital) {
        url += `?capital_restant_du=${capital}`;
      }
      const res = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Demande de remboursement anticipé générée");
      setShowSimulationModal(false);
      navigate(`/engagements/resiliations?id=${res.data.resiliation_id}`);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Erreur de génération";
      toast.error(typeof errorMessage === 'string' ? errorMessage : "Erreur de génération de la lettre");
    } finally {
      setGeneratingLetter(false);
    }
  };

  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteOptions, setDeleteOptions] = useState({
    delete_vehicle: false,
    delete_vehicle_contract: true,
    delete_resiliations: true,
  });

  const openDeleteModal = async () => {
    setShowDeleteModal(true);
    setDeletePreview(null);
    try {
      const res = await axios.get(`${API}/api/engagements/contrats/${id}/delete-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeletePreview(res.data);
      // Par défaut, propose la suppression du véhicule s'il deviendrait orphelin
      setDeleteOptions({
        delete_vehicle: !!res.data?.vehicle?.would_be_orphan,
        delete_vehicle_contract: !!res.data?.vehicle_contract,
        delete_resiliations: (res.data?.resiliations_count || 0) > 0,
      });
    } catch (e) {
      // fallback silencieux
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const params = new URLSearchParams({
        delete_vehicle: deleteOptions.delete_vehicle,
        delete_vehicle_contract: deleteOptions.delete_vehicle_contract,
        delete_resiliations: deleteOptions.delete_resiliations,
      });
      const res = await axios.delete(`${API}/api/engagements/contrats/${id}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const c = res.data?.cascade || {};
      const parts = [];
      if (c.vehicle_deleted) parts.push("véhicule supprimé");
      else if (c.vehicle_dissociated) parts.push("véhicule dissocié");
      if (c.vehicle_contract_deleted) parts.push("contrat véhicule archivé");
      if (c.resiliations_deleted) parts.push(`${c.resiliations_deleted} résiliation(s)`);
      toast.success(
        parts.length ? `Contrat supprimé (${parts.join(", ")})` : "Contrat supprimé"
      );
      navigate("/engagements/contrats");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleArchive = async () => {
    try {
      await axios.put(`${API}/api/engagements/contrats/${id}`, {
        statut: "resilier"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contrat archivé (marqué comme résilié)");
      fetchContrat();
    } catch (error) {
      toast.error("Erreur lors de l'archivage");
    }
  };

  const handleDuplicate = async () => {
    try {
      const duplicateData = {
        nom_contrat: `${contrat.nom_contrat} (copie)`,
        prestataire: contrat.prestataire,
        categorie: contrat.categorie,
        numero_contrat: null,
        montant_mensuel_ht: contrat.montant_mensuel_ht,
        montant_annuel_ht: contrat.montant_annuel_ht,
        date_debut: contrat.date_debut,
        date_echeance: contrat.date_echeance,
        duree_engagement_mois: contrat.duree_engagement_mois,
        delai_preavis_jours: contrat.delai_preavis_jours,
        mode_resiliation: contrat.mode_resiliation,
        adresse_prestataire: contrat.adresse_prestataire
      };
      
      const res = await axios.post(`${API}/api/engagements/contrats`, duplicateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contrat dupliqué");
      navigate(`/engagements/contrats/${res.data.id}`);
    } catch (error) {
      toast.error("Erreur lors de la duplication");
    }
  };

  const handleViewOriginalDocument = async () => {
    if (!contrat.fichier_original_url) {
      toast.info("Aucun document original attaché à ce contrat");
      return;
    }
    setShowPdfViewer(true);
  };

  const handleHistoryClick = (entry) => {
    // Navigate to resiliation if available
    if (entry.action === "generation_lettre" && entry.metadata?.resiliation_id) {
      navigate(`/engagements/resiliations?id=${entry.metadata.resiliation_id}`);
    }
  };

  const handleDeleteResiliation = async (resiliationId) => {
    if (!window.confirm("Supprimer cette lettre de résiliation brouillon ?")) return;
    
    try {
      await axios.delete(`${API}/api/engagements/resiliations/${resiliationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lettre de résiliation supprimée");
      fetchContrat(); // Refresh to update history
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-64 space-y-4">
        <div className="relative">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
          <FileText className="w-6 h-6 text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="text-center">
          <p className="text-slate-600 font-medium">Chargement du contrat...</p>
          <p className="text-slate-400 text-sm mt-1">Récupération des informations</p>
        </div>
      </div>
    );
  }

  if (!contrat) return null;

  const cat = categoryIcons[contrat.categorie] || categoryIcons.autre;
  const Icon = cat.icon;
  const isUrgent = contrat.jours_avant_preavis != null && contrat.jours_avant_preavis <= 60;

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="contrat-detail">
      {/* Header - Responsive */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/engagements/contrats")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg ${cat.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 md:w-6 md:h-6 ${cat.color}`} />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg md:text-2xl font-bold text-slate-900 truncate">{contrat.prestataire}</h1>
            <div className="flex flex-wrap items-center gap-1 md:gap-2 text-sm text-slate-500">
              <span className="truncate max-w-[150px] md:max-w-none">{contrat.nom_contrat}</span>
              {contrat.numero_contrat && (
                <>
                  <span>•</span>
                  <span className="font-mono text-xs md:text-sm">N° {contrat.numero_contrat}</span>
                </>
              )}
            </div>
            {vehicleLink?.linked && vehicleLink.vehicle && (
              <button
                type="button"
                onClick={() => navigate(`/fleet/vehicles/${vehicleLink.vehicle.id}`)}
                className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs hover:bg-blue-100 transition"
                data-testid="linked-vehicle-badge"
                title="Véhicule associé dans Parc Auto — cliquez pour voir"
              >
                <Car className="w-3 h-3" />
                Véhicule : {vehicleLink.vehicle.registration || vehicleLink.vehicle.license_plate || "N/A"} — {vehicleLink.vehicle.brand} {vehicleLink.vehicle.model}
              </button>
            )}
            {vehicleLink && !vehicleLink.linked && ["leasing", "assurance", "maintenance"].includes(contrat.categorie) && (
              <button
                type="button"
                onClick={() => setShowVehicleDialog(true)}
                className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs hover:bg-indigo-100 transition"
                data-testid="link-vehicle-btn"
                title="Créer ou lier ce contrat à un véhicule du Parc Auto"
              >
                <Car className="w-3 h-3" />
                Lier au Parc Auto
              </button>
            )}
          </div>
        </div>
        
        {/* Action buttons - Responsive grid */}
        <div className="flex flex-wrap gap-2">
          {contrat.fichier_original_url && (
            <Button variant="outline" size="sm" onClick={handleViewOriginalDocument}>
              <FileText className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Voir document</span>
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDuplicate}>
                <Copy className="w-4 h-4 mr-2" />
                Dupliquer
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleArchive}>
                <Archive className="w-4 h-4 mr-2" />
                Archiver (marquer résilié)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={openDeleteModal}
                className="text-red-600 focus:text-red-600"
                data-testid="delete-contract-menuitem"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* CONDITIONAL BUTTONS based on contract type */}
          {contrat.is_loan ? (
            <>
              {/* LOAN-SPECIFIC BUTTONS */}
              <Button variant="outline" size="sm" onClick={() => { setShowSimulationModal(true); setCapitalInput(contrat.capital_restant_du || contrat.capital_emprunte || ""); }}>
                <Landmark className="w-4 h-4 md:mr-2" />
                <span className="hidden sm:inline">Simuler remboursement</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowConcurrenceModal(true)}>
                <Users className="w-4 h-4 md:mr-2" />
                <span className="hidden sm:inline">Rachat de crédit</span>
              </Button>
              <Button size="sm" onClick={handleGenerateEarlyRepaymentLetter} disabled={generatingLetter}>
                {generatingLetter ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Mail className="w-4 h-4 md:mr-2" />
                    <span className="hidden sm:inline">Demande remboursement</span>
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              {/* STANDARD CONTRACT BUTTONS */}
              <Button variant="outline" size="sm" onClick={() => setShowConcurrenceModal(true)}>
                <Users className="w-4 h-4 md:mr-2" />
                <span className="hidden sm:inline">Jouer la concurrence</span>
              </Button>
              <Button size="sm" onClick={handleGenerateResiliation} disabled={generatingLetter}>
                {generatingLetter ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Mail className="w-4 h-4 md:mr-2" />
                    <span className="hidden sm:inline">Générer résiliation</span>
                  </>
                )}
              </Button>
              {/* Bouton import demande résiliation CLIENT */}
              <ResiliationClientUpload contrat={contrat} onSuccess={fetchContrat} />
            </>
          )}
        </div>
      </div>

      {/* Loading overlay for letter generation */}
      {generatingLetter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-96 p-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="relative">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
                <FileText className="w-6 h-6 text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-slate-900">Génération en cours...</h3>
                <p className="text-slate-500 text-sm mt-1">
                  {contrat.is_loan 
                    ? "L'intelligence artificielle rédige votre demande de remboursement anticipé."
                    : "L'intelligence artificielle rédige votre lettre de résiliation."
                  }
                </p>
                <p className="text-slate-400 text-xs mt-2">
                  Cela peut prendre quelques secondes.
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* LOAN SIMULATION MODAL */}
      <Dialog open={showSimulationModal} onOpenChange={setShowSimulationModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-emerald-600" />
              Simulation de remboursement anticipé
            </DialogTitle>
            <DialogDescription>
              Calculez le coût d'un remboursement anticipé de votre prêt
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Capital input */}
            <div className="space-y-2">
              <Label>Capital restant dû (€)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={capitalInput}
                  onChange={(e) => setCapitalInput(e.target.value)}
                  placeholder="Ex: 20000"
                />
                <Button onClick={handleSimulateRepayment} disabled={simulationLoading || !capitalInput}>
                  {simulationLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Calculer"}
                </Button>
              </div>
            </div>

            {/* Simulation Results */}
            {simulation && (
              <div className="space-y-4 mt-4">
                <Separator />
                
                {/* Main figures */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-4 bg-slate-50">
                    <p className="text-sm text-slate-500">Capital à rembourser</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(simulation.capital_restant_du)}</p>
                  </Card>
                  <Card className={`p-4 ${simulation.remboursement_gratuit ? 'bg-green-50' : 'bg-amber-50'}`}>
                    <p className="text-sm text-slate-500">Indemnité de remboursement anticipé (IRA)</p>
                    <p className={`text-2xl font-bold ${simulation.remboursement_gratuit ? 'text-green-600' : 'text-amber-600'}`}>
                      {simulation.remboursement_gratuit ? 'GRATUIT' : formatCurrency(simulation.indemnite_remboursement_anticipe)}
                    </p>
                  </Card>
                </div>

                <Card className="p-4 bg-blue-50 border-blue-200">
                  <p className="text-sm text-blue-600">Montant total à payer</p>
                  <p className="text-3xl font-bold text-blue-700">{formatCurrency(simulation.montant_total_a_payer)}</p>
                </Card>

                {/* Advice */}
                <Alert className={simulation.remboursement_gratuit ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}>
                  <AlertDescription className={simulation.remboursement_gratuit ? "text-green-700" : "text-amber-700"}>
                    {simulation.conseil}
                  </AlertDescription>
                </Alert>

                {/* Competition advice */}
                <Alert className="bg-slate-50 border-slate-200">
                  <Users className="text-slate-600 w-4 h-4" />
                  <AlertDescription className="text-slate-700">
                    {simulation.conseil_concurrence}
                  </AlertDescription>
                </Alert>

                {/* Action button */}
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowSimulationModal(false)}>
                    Fermer
                  </Button>
                  <Button onClick={handleGenerateEarlyRepaymentLetter} disabled={generatingLetter}>
                    {generatingLetter ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2" />
                    )}
                    Générer la demande de remboursement
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Urgency Alert */}
      {contrat.preavis_statut === "trop_tard_periode_suivante" && (
        <Alert className="bg-blue-50 border-blue-200">
          <Clock className="text-blue-600" />
          <AlertDescription className="text-blue-700">
            <strong>Info :</strong> La date limite pour résilier cette année est passée. 
            Prochaine opportunité : <strong>{formatDate(contrat.date_limite_preavis)}</strong> (pour une résiliation effective le {formatDate(contrat.date_resiliation_possible)})
          </AlertDescription>
        </Alert>
      )}
      {isUrgent && contrat.preavis_statut !== "trop_tard_periode_suivante" && !contrat.is_loan && (
        <Alert className={contrat.jours_avant_preavis <= 30 ? "bg-red-50 border-red-200" : "bg-orange-50 border-orange-200"}>
          <AlertTriangle className={contrat.jours_avant_preavis <= 30 ? "text-red-600" : "text-orange-600"} />
          <AlertDescription className={contrat.jours_avant_preavis <= 30 ? "text-red-700" : "text-orange-700"}>
            <strong>{contrat.jours_avant_preavis <= 30 ? "URGENT" : "Attention"} !</strong> Date limite de préavis dans {contrat.jours_avant_preavis} jours ({formatDate(contrat.date_limite_preavis)})
          </AlertDescription>
        </Alert>
      )}

      {/* Tacite reconduction — état actuel */}
      {(contrat.en_reconduction || contrat.reconduction_tacite === true) && (
        <Alert className="bg-purple-50 border-purple-200" data-testid="reconduction-alert">
          <RefreshCw className="text-purple-600" />
          <AlertDescription className="text-purple-800">
            {contrat.en_reconduction ? (
              <>
                <strong>🔁 Ce contrat est actuellement en tacite reconduction.</strong>
                {contrat.date_reconduction_actuelle && (
                  <span> Reconduit depuis le <strong>{formatDate(contrat.date_reconduction_actuelle)}</strong>.</span>
                )}
                {contrat.duree_reconduction_mois && (
                  <span> Durée : <strong>{contrat.duree_reconduction_mois} mois</strong>.</span>
                )}
                <span> Prochaine échéance : <strong>{formatDate(contrat.date_echeance)}</strong>.</span>
                {contrat.date_limite_preavis && (
                  <span> Préavis avant le <strong>{formatDate(contrat.date_limite_preavis)}</strong>.</span>
                )}
                {(contrat.historique_reconductions || []).length > 0 && (
                  <span className="block mt-1 text-xs text-purple-600">
                    {contrat.historique_reconductions.length} reconduction{contrat.historique_reconductions.length > 1 ? "s" : ""} historique · Dernière détectée le {formatDate((contrat.historique_reconductions.at(-1) || {}).detected_at)}
                  </span>
                )}
              </>
            ) : (
              <>
                <strong>Contrat à tacite reconduction.</strong> Durée de reconduction : <strong>{contrat.duree_reconduction_mois || "?"} mois</strong> · Préavis : <strong>{contrat.preavis_reconduction_jours || contrat.delai_preavis_jours || "?"} jours</strong>. Surveillez la date limite pour ne pas rater le préavis.
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Carte Résiliation Client (si en cours ou effectuée) */}
      <ResiliationClientCard contrat={contrat} onRefresh={fetchContrat} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: AI Summary */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Résumé IA
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={handleRegenerateSummary} disabled={regenerating}>
                {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 whitespace-pre-line">
              {contrat.resume_ia || "Aucun résumé disponible. Cliquez sur le bouton pour en générer un."}
            </p>
            
            {contrat.clauses && contrat.clauses.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-medium text-slate-700 mb-2">Clauses importantes :</p>
                <ul className="space-y-1">
                  {contrat.clauses.map((clause, i) => (
                    <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                      <span className="text-blue-500">•</span>
                      {clause.description || clause}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Middle: Contract Data */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Informations du contrat</CardTitle>
              {editMode ? (
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setEditMode(false); setFormData(contrat); }}>
                    <X className="w-4 h-4 mr-1" /> Annuler
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Enregistrer
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                  <Edit2 className="w-4 h-4 mr-1" /> Modifier
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {editMode ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Prestataire *</Label><Input value={formData.prestataire || ""} onChange={e => setFormData({...formData, prestataire: e.target.value})} /></div>
                  <div><Label>Nom du contrat *</Label><Input value={formData.nom_contrat || ""} onChange={e => setFormData({...formData, nom_contrat: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Numéro de contrat/police</Label><Input value={formData.numero_contrat || ""} onChange={e => setFormData({...formData, numero_contrat: e.target.value})} placeholder="Ex: AXA-PRO-2025-001" /></div>
                  <div>
                    <Label>Catégorie</Label>
                    <Select value={formData.categorie || "autre"} onValueChange={v => setFormData({...formData, categorie: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(categoryIcons).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Montant mensuel HT (€)</Label><Input type="number" step="0.01" value={formData.montant_mensuel_ht || ""} onChange={e => setFormData({...formData, montant_mensuel_ht: e.target.value})} /></div>
                  <div><Label>Montant annuel HT (€)</Label><Input type="number" step="0.01" value={formData.montant_annuel_ht || ""} onChange={e => setFormData({...formData, montant_annuel_ht: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Date début</Label><Input type="date" value={formData.date_debut || ""} onChange={e => setFormData({...formData, date_debut: e.target.value})} /></div>
                  <div><Label>Date échéance</Label><Input type="date" value={formData.date_echeance || ""} onChange={e => setFormData({...formData, date_echeance: e.target.value})} /></div>
                  <div><Label>Durée engagement (mois)</Label><Input type="number" value={formData.duree_engagement_mois || ""} onChange={e => setFormData({...formData, duree_engagement_mois: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Délai préavis (jours)</Label><Input type="number" value={formData.delai_preavis_jours || ""} onChange={e => setFormData({...formData, delai_preavis_jours: e.target.value})} /></div>
                  <div><Label>Date limite préavis</Label><Input type="date" value={formData.date_limite_preavis || ""} onChange={e => setFormData({...formData, date_limite_preavis: e.target.value})} /></div>
                  <div>
                    <Label>Mode de résiliation</Label>
                    <Select value={formData.mode_resiliation || ""} onValueChange={v => setFormData({...formData, mode_resiliation: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lrar">LRAR (Recommandée)</SelectItem>
                        <SelectItem value="lettre_simple">Lettre simple</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Adresse prestataire</Label><Textarea value={formData.adresse_prestataire || ""} onChange={e => setFormData({...formData, adresse_prestataire: e.target.value})} rows={2} placeholder="Adresse complète pour l'envoi des courriers" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Email prestataire</Label><Input type="email" value={formData.email_prestataire || ""} onChange={e => setFormData({...formData, email_prestataire: e.target.value})} placeholder="resiliation@prestataire.com" /></div>
                  <div><Label>Téléphone prestataire</Label><Input type="tel" value={formData.telephone_prestataire || ""} onChange={e => setFormData({...formData, telephone_prestataire: e.target.value})} placeholder="01 23 45 67 89" /></div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  {contrat.numero_contrat && (
                    <div className="col-span-2 flex justify-between pb-2 border-b">
                      <span className="text-slate-500">N° Contrat/Police</span>
                      <span className="font-mono font-semibold text-blue-600">{contrat.numero_contrat}</span>
                    </div>
                  )}
                  <div className="flex justify-between"><span className="text-slate-500">Catégorie</span><Badge className={`${cat.bg} ${cat.color}`}>{cat.label}</Badge></div>
                  <div className="flex justify-between"><span className="text-slate-500">Statut</span><Badge variant="outline" className={contrat.statut === "resilier" ? "bg-gray-100" : "bg-green-50 text-green-700"}>{contrat.statut === "resilier" ? "Résilié" : "Actif"}</Badge></div>
                  <div className="flex justify-between"><span className="text-slate-500">Montant mensuel</span><span className="font-medium">{formatCurrency(contrat.montant_mensuel_ht)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Montant annuel</span><span className="font-medium">{formatCurrency(contrat.montant_annuel_ht)}</span></div>
                  {!contrat.montant_mensuel_ht && !contrat.montant_annuel_ht && (
                    <div className="col-span-2 -mt-1">
                      <button
                        type="button"
                        onClick={handleReExtractAmounts}
                        disabled={reExtracting}
                        className="w-full text-xs px-2 py-1.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 flex items-center justify-center gap-1"
                        data-testid="re-extract-amounts-btn"
                      >
                        {reExtracting ? "Analyse en cours…" : "Ré-extraire les montants depuis le PDF"}
                      </button>
                    </div>
                  )}
                  <div className="flex justify-between"><span className="text-slate-500">Date début</span><span>{formatDate(contrat.date_debut)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Date échéance</span><span>{formatDate(contrat.date_echeance)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Durée engagement</span><span>{contrat.duree_engagement_mois ? `${contrat.duree_engagement_mois} mois` : "-"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Délai préavis</span><span>{contrat.delai_preavis_jours ? `${contrat.delai_preavis_jours} jours` : "-"}</span></div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Date limite préavis</span>
                    <div className="text-right">
                      <span className={isUrgent ? "font-medium text-red-600" : ""}>{formatDate(contrat.date_limite_preavis)}</span>
                      {contrat.jours_avant_preavis !== null && contrat.jours_avant_preavis !== undefined && (
                        <span className={`text-xs ml-2 ${contrat.jours_avant_preavis <= 30 ? "text-red-500" : contrat.jours_avant_preavis <= 60 ? "text-orange-500" : "text-slate-400"}`}>
                          (J-{contrat.jours_avant_preavis})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Prochaine échéance</span>
                    <span className="font-medium text-blue-600">{formatDate(contrat.date_resiliation_possible)}</span>
                  </div>
                  <div className="flex justify-between"><span className="text-slate-500">Mode résiliation</span><span className="capitalize">{contrat.mode_resiliation || "-"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Reconduction tacite</span><span>{contrat.reconduction_tacite !== false ? "Oui" : "Non"}</span></div>
                </div>
                {contrat.adresse_prestataire && (
                  <div className="pt-4 border-t">
                    <span className="text-slate-500 text-sm">Adresse prestataire</span>
                    <p className="text-slate-700 mt-1">{contrat.adresse_prestataire}</p>
                  </div>
                )}
                {(contrat.email_prestataire || contrat.telephone_prestataire) && (
                  <div className="pt-4 border-t grid grid-cols-2 gap-4">
                    {contrat.email_prestataire && (
                      <div>
                        <span className="text-slate-500 text-sm">Email</span>
                        <p className="text-blue-600 font-medium mt-1">{contrat.email_prestataire}</p>
                      </div>
                    )}
                    {contrat.telephone_prestataire && (
                      <div>
                        <span className="text-slate-500 text-sm">Téléphone</span>
                        <p className="text-slate-700 mt-1">{contrat.telephone_prestataire}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* LOAN-SPECIFIC INFO SECTION */}
            {!editMode && contrat.is_loan && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <Landmark className="w-5 h-5 text-emerald-600" />
                  Informations financières du prêt
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {contrat.capital_emprunte && (
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <span className="text-slate-500 text-sm">Capital emprunté</span>
                      <p className="text-lg font-semibold text-slate-900">{formatCurrency(contrat.capital_emprunte)}</p>
                    </div>
                  )}
                  {contrat.capital_restant_du && (
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <span className="text-blue-600 text-sm">Capital restant dû</span>
                      <p className="text-lg font-semibold text-blue-700">{formatCurrency(contrat.capital_restant_du)}</p>
                    </div>
                  )}
                  {contrat.taux_interet && (
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <span className="text-slate-500 text-sm">Taux d'intérêt</span>
                      <p className="text-lg font-semibold text-slate-900">{contrat.taux_interet}%</p>
                    </div>
                  )}
                  {contrat.taux_effectif_global && (
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <span className="text-slate-500 text-sm">TEG/TAEG</span>
                      <p className="text-lg font-semibold text-slate-900">{contrat.taux_effectif_global}%</p>
                    </div>
                  )}
                </div>
                
                {/* Early repayment info */}
                <div className="mt-4 p-4 rounded-lg border bg-amber-50 border-amber-200">
                  <h4 className="font-medium text-amber-800 mb-2">Remboursement anticipé</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-amber-700">Possible :</span>
                      <span className="ml-2 font-medium">{contrat.remboursement_anticipe_possible !== false ? "Oui" : "Non"}</span>
                    </div>
                    <div>
                      <span className="text-amber-700">Gratuit :</span>
                      <span className={`ml-2 font-medium ${contrat.remboursement_anticipe_gratuit ? "text-green-600" : "text-amber-700"}`}>
                        {contrat.remboursement_anticipe_gratuit ? "Oui (sans indemnité)" : "Non"}
                      </span>
                    </div>
                    {contrat.indemnite_remboursement_anticipe && (
                      <div className="col-span-2">
                        <span className="text-amber-700">Indemnité :</span>
                        <span className="ml-2 font-medium">{contrat.indemnite_remboursement_anticipe}% du capital remboursé</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Smart advice for loan */}
                {contrat.remboursement_anticipe_gratuit && (
                  <Alert className="mt-4 bg-green-50 border-green-200">
                    <Sparkles className="text-green-600 w-4 h-4" />
                    <AlertDescription className="text-green-700">
                      <strong>Opportunité :</strong> Le remboursement anticipé est gratuit ! Si vous disposez de trésorerie excédentaire ou si un concurrent propose un meilleur taux, c'est le moment d'agir.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* History */}
      {contrat.historique && contrat.historique.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historique des actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {contrat.historique.slice().reverse().map((entry, i) => {
                const isClickable = (entry.action === "generation_lettre" || entry.action === "generation_demande_remboursement") && entry.metadata?.resiliation_id;
                const isDeletable = (entry.action === "generation_lettre" || entry.action === "generation_demande_remboursement") && entry.metadata?.resiliation_id;
                return (
                  <div 
                    key={i} 
                    className={`flex items-center gap-4 p-3 rounded-lg ${
                      isClickable 
                        ? "bg-blue-50 hover:bg-blue-100 border border-blue-200" 
                        : "bg-slate-50"
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full ${
                      entry.action === "generation_lettre" ? "bg-purple-500" : 
                      entry.action === "envoi_email" ? "bg-green-500" : "bg-blue-500"
                    }`} />
                    <div 
                      className={`flex-1 ${isClickable ? "cursor-pointer" : ""}`}
                      onClick={() => isClickable && handleHistoryClick(entry)}
                    >
                      <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        {entry.description}
                        {isClickable && (
                          <span className="text-xs text-blue-600 font-normal">(Cliquez pour voir)</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500">
                        {entry.user_name} • {new Date(entry.timestamp).toLocaleString("fr-FR")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isClickable && (
                        <FileText className="w-4 h-4 text-blue-500" />
                      )}
                      {isDeletable && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteResiliation(entry.metadata.resiliation_id);
                          }}
                          title="Supprimer la lettre brouillon"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Concurrence Modal */}
      <Dialog open={showConcurrenceModal} onOpenChange={setShowConcurrenceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Jouer la concurrence</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              Nous travaillons à des partenariats avec des courtiers et prestataires pour vous permettre d'obtenir des devis compétitifs en un clic, directement depuis vos contrats à renouveler.
            </p>
            <div className="space-y-2">
              {[
                { emoji: "🛡️", label: "Assurances professionnelles", status: "Partenariat en cours" },
                { emoji: "📱", label: "Téléphonie entreprise", status: "Négociation opérateurs Q4 2025" },
                { emoji: "🚗", label: "Leasing & flotte automobile", status: "Partenaires LLD, intégration 2026" },
                { emoji: "☁️", label: "SaaS & licences logicielles", status: "En cours" },
                { emoji: "💡", label: "Énergie (électricité, gaz)", status: "En cours" }
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span>{item.emoji} {item.label}</span>
                  <Badge variant="outline">Bientôt</Badge>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConcurrenceModal(false)}>Fermer</Button>
            <Button onClick={handleConcurrenceIntent}>Je suis intéressé(e)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal — Cascade */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-lg" data-testid="delete-cascade-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Supprimer ce contrat — que faut-il conserver ?
            </DialogTitle>
            <DialogDescription>
              Contrat <span className="font-medium">&quot;{contrat.nom_contrat}&quot;</span> ({contrat.prestataire}).
              Choisissez ce qui doit être supprimé en cascade. Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>

          {!deletePreview ? (
            <div className="py-6 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-3 my-2">
              {/* Véhicule Parc Auto */}
              {deletePreview.vehicle ? (
                <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deleteOptions.delete_vehicle}
                    onChange={(e) => setDeleteOptions((o) => ({ ...o, delete_vehicle: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600"
                    data-testid="cascade-delete-vehicle"
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      🚗 Supprimer le véhicule Parc Auto
                      {deletePreview.vehicle.would_be_orphan ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                          orphelin
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                          {deletePreview.vehicle.other_engagements_count} autre(s) engagement(s)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {deletePreview.vehicle.brand} {deletePreview.vehicle.model} · {deletePreview.vehicle.registration} · {deletePreview.vehicle.internal_code}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {deletePreview.vehicle.would_be_orphan
                        ? "Ce contrat est le seul engagement du véhicule — il peut être supprimé."
                        : "Le véhicule sera simplement dissocié (d'autres engagements y sont liés)."}
                    </div>
                  </div>
                </label>
              ) : (
                <div className="p-3 rounded-lg bg-slate-50 text-xs text-slate-500">
                  Pas de véhicule Parc Auto lié à ce contrat.
                </div>
              )}

              {/* Vehicle contract */}
              {deletePreview.vehicle_contract && (
                <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deleteOptions.delete_vehicle_contract}
                    onChange={(e) => setDeleteOptions((o) => ({ ...o, delete_vehicle_contract: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600"
                    data-testid="cascade-delete-vc"
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-medium text-slate-900">
                      📄 Archiver le contrat véhicule (LLD/LOA/assurance)
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Type {deletePreview.vehicle_contract.contract_type?.toUpperCase()} · {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(deletePreview.vehicle_contract.monthly_rent_ht || 0)}/mois HT
                    </div>
                  </div>
                </label>
              )}

              {/* Résiliations */}
              {deletePreview.resiliations_count > 0 ? (
                <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deleteOptions.delete_resiliations}
                    onChange={(e) => setDeleteOptions((o) => ({ ...o, delete_resiliations: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600"
                    data-testid="cascade-delete-resil"
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-medium text-slate-900">
                      ✉️ Supprimer les lettres de résiliation ({deletePreview.resiliations_count})
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Y compris brouillons et LRAR envoyées via l&apos;IA.
                    </div>
                  </div>
                </label>
              ) : (
                <div className="p-3 rounded-lg bg-slate-50 text-xs text-slate-500">
                  Aucune lettre de résiliation liée.
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting} data-testid="cascade-cancel-btn">
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting || !deletePreview}
              data-testid="cascade-confirm-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Modal */}
      <Dialog open={showPdfViewer} onOpenChange={setShowPdfViewer}>
        <DialogContent className="max-w-4xl h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Document original - {contrat.fichier_original_url}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full min-h-[500px]">
            <PdfViewer contractId={id} token={token} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Vehicle validation dialog — create or link to Parc Auto */}
      <VehicleValidationDialog
        open={showVehicleDialog}
        onOpenChange={setShowVehicleDialog}
        engagementId={id}
        extractedVehicleInfo={contrat?.vehicle_info || {}}
        extractedContractInfo={contrat?.vehicle_contract_info || {}}
        token={token}
        onLinked={() => {
          setShowVehicleDialog(false);
          fetchVehicleLink();
          toast.success("Contrat lié au Parc Auto");
        }}
        onSkip={() => setShowVehicleDialog(false)}
      />
    </div>
  );
}

// PDF Viewer Component
function PdfViewer({ contractId, token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    const loadDocument = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${API}/api/engagements/contrats/${contractId}/document`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.data.success && res.data.document) {
          const { base64, content_type } = res.data.document;
          const binary = atob(base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: content_type });
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
        }
      } catch (err) {
        console.error("Error loading document:", err);
        setError(err.response?.data?.detail || "Erreur lors du chargement du document");
      } finally {
        setLoading(false);
      }
    };
    
    loadDocument();
    
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [contractId, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        {error}
      </div>
    );
  }

  return (
    <iframe 
      src={pdfUrl} 
      className="w-full h-full min-h-[500px] rounded border"
      title="Document original"
    />
  );
}
